import { jsPDF } from 'jspdf';
import moment from 'moment';
import { shortDayLabel } from '../../utils/appDate';

/**
 * A customer's statement, as a PDF.
 *
 * This is the desktop's answer to the phone's Share button. The phone hands
 * the invoice to WhatsApp, which a till usually doesn't have — but the thing
 * people actually want is the same either way: a sheet the customer can be
 * given or sent, showing every line and what is left owing.
 *
 * Drawn by hand rather than with a table plugin, matching exportOrdersPdf —
 * the app ships no table library and one layout doesn't justify a dependency.
 */

const PAGE = { width: 595.28, height: 841.89 }; // A4 portrait, points
const MARGIN = 40;
const LINE = 17;

const COLUMNS = [
  { key: 'description', label: 'Description', x: MARGIN, align: 'left', width: 210 },
  { key: 'date', label: 'Date', x: MARGIN + 220, align: 'left', width: 90 },
  { key: 'amount', label: 'Amount', x: PAGE.width - MARGIN - 95, align: 'right', width: 80 },
  { key: 'balance', label: 'Balance', x: PAGE.width - MARGIN, align: 'right', width: 80 },
];

function drawHeaderRow(doc, y) {
  doc.setFontSize(8).setFont('helvetica', 'bold').setTextColor(120);
  COLUMNS.forEach((col) => {
    doc.text(col.label.toUpperCase(), col.x, y, { align: col.align });
  });
  doc.setDrawColor(210).line(MARGIN, y + 5, PAGE.width - MARGIN, y + 5);
  return y + LINE;
}

/**
 * @param {Object} input
 * @param {Object} input.customer      the customer, with their current balance
 * @param {Array}  input.transactions  already carrying a running `balance`
 * @param {Object} input.business      for the letterhead and currency mark
 * @returns {string} the filename it saved
 */
export default function customerStatementPdf({ customer, transactions, business }) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const currency = business?.currencyMark || '$';

  let y = MARGIN;

  // ── Letterhead ───────────────────────────────────────────────────────────
  doc.setFontSize(15).setFont('helvetica', 'bold').setTextColor(20);
  doc.text(business?.businessName || business?.name || 'Dalabtire', MARGIN, y);
  y += 18;

  doc.setFontSize(10).setFont('helvetica', 'normal').setTextColor(90);
  doc.text('Customer statement', MARGIN, y);
  doc.text(moment().format('D MMM YYYY'), PAGE.width - MARGIN, y, { align: 'right' });
  y += 24;

  // ── Who ──────────────────────────────────────────────────────────────────
  doc.setFontSize(13).setFont('helvetica', 'bold').setTextColor(20);
  doc.text(customer?.name || '', MARGIN, y);
  y += 15;

  const meta = [customer?.phone, customer?.district].filter(Boolean).join('  ·  ');
  if (meta) {
    doc.setFontSize(9.5).setFont('helvetica', 'normal').setTextColor(120);
    doc.text(meta, MARGIN, y);
    y += 14;
  }
  y += 6;

  // ── What is owed ─────────────────────────────────────────────────────────
  doc.setDrawColor(225).setFillColor(247, 247, 249);
  doc.rect(MARGIN, y, PAGE.width - MARGIN * 2, 46, 'F');

  doc.setFontSize(7.5).setFont('helvetica', 'normal').setTextColor(130);
  doc.text('BALANCE', MARGIN + 12, y + 17);
  doc.setFontSize(16).setFont('helvetica', 'bold').setTextColor(20);
  doc.text(
    `${currency}${Number(customer?.balance || 0).toFixed(2)}`,
    MARGIN + 12,
    y + 36
  );

  doc.setFontSize(7.5).setFont('helvetica', 'normal').setTextColor(130);
  doc.text('ENTRIES', PAGE.width - MARGIN - 12, y + 17, { align: 'right' });
  doc.setFontSize(16).setFont('helvetica', 'bold').setTextColor(20);
  doc.text(String(transactions.length), PAGE.width - MARGIN - 12, y + 36, { align: 'right' });

  y += 66;

  // ── Lines ────────────────────────────────────────────────────────────────
  y = drawHeaderRow(doc, y);

  transactions.forEach((transaction) => {
    if (y > PAGE.height - MARGIN - LINE) {
      doc.addPage();
      y = MARGIN;
      y = drawHeaderRow(doc, y);
    }

    const isCharge = Number(transaction.debit || 0) > 0;
    const amount = isCharge ? transaction.debit : transaction.credit;

    const cells = {
      description: transaction.description || '',
      date: shortDayLabel(transaction.date),
      amount: `${currency}${Number(amount || 0).toFixed(2)}`,
      balance: `${currency}${Number(transaction.balance || 0).toFixed(2)}`,
    };

    doc.setFontSize(9).setFont('helvetica', 'normal');
    COLUMNS.forEach((col) => {
      const value = String(cells[col.key] || '');
      if (!value) return;

      if (col.key === 'amount') {
        // A charge and a payment must be tellable apart at a glance, which is
        // the whole reason someone reads a statement.
        if (isCharge) doc.setTextColor(196, 52, 26);
        else doc.setTextColor(22, 128, 61);
      } else if (col.key === 'balance') {
        doc.setTextColor(35);
      } else {
        doc.setTextColor(60);
      }

      doc.setFont('helvetica', col.key === 'balance' ? 'bold' : 'normal');
      const text = doc.splitTextToSize(value, col.width)[0];
      doc.text(text, col.x, y, { align: col.align });
    });

    doc.setDrawColor(238).line(MARGIN, y + 4, PAGE.width - MARGIN, y + 4);
    y += LINE;
  });

  const safeName = String(customer?.name || 'customer')
    .replace(/[^a-z0-9]+/gi, '-')
    .toLowerCase();
  const filename = `statement-${safeName}-${moment().format('YYYY-MM-DD')}.pdf`;

  doc.save(filename);
  return filename;
}
